// Copyright (c) Microgreen Manager LLC. All rights reserved. Proprietary and confidential. Unauthorized copying is strictly prohibited.
import { useLazyQuery, useMutation } from '@apollo/client';
import { clientLogger } from '@seedplanner/logger';
import gql from 'graphql-tag';
import { enqueueSnackbar } from 'notistack';
import * as React from 'react';
import { useUserContext } from '../../context/UserContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '../../lib/base';
import { Help } from '../../lib/help';
import { DASHBOARD_QUERY } from '../../queries/DashboardQueries';
import type {
  EditCropBufferPercentDataQuery,
  EditCropBufferPercentDataQueryVariables,
  EditCropBufferPercentMutation,
  EditCropBufferPercentMutationVariables,
} from '../../types/__generated__/graphql';
import { getNumericID, getTimeRanges, validateNumInput } from '../../utils';
import { ExitDialogButton } from '../common/button';
import { ErrorView } from '../common/error';
import { PageLoading } from '../common/loading';
import { formatCropDialogTitle } from './formatCropDialogTitle';

const EDIT_CROP_BUFFER_PERCENT_MUTATION = gql`
  mutation EditCropBufferPercent(
    $crop_id: Int!
    $buffer_percentage: Int!
  ) {
    editCropBufferPercent(
      input: {
        crop_id: $crop_id
        buffer_percentage: $buffer_percentage
      }
    ) {
      updated_crop {
        id
        buffer_percentage
        updated_at
      }
      updated_plantings {
        id
        trays_rounded
        updated_at
      }
    }
  }
`;

const EDIT_CROP_BUFFER_PERCENT_DATA = gql`
  query EditCropBufferPercentData($id: ID!) {
    crop(id: $id) {
      id
      buffer_percentage
      plantings(filters: [{ name: "planting.planting_status", operator: "=", value: "planned" }]) {
        edges {
          node {
            id
            planting_time
            planting_status
            trays_rounded
          }
        }
      }
    }
  }
`;

interface Props {
  readonly cropId: string;
  readonly cropName: string;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onSuccess: () => void;
}

export default function EditCropBufferPercentDialog({ isOpen, onClose, onSuccess, cropId, cropName }: Props) {
  const [fetchCrop, { data, error: dataError, loading: cropDataLoading }] = useLazyQuery<
    EditCropBufferPercentDataQuery,
    EditCropBufferPercentDataQueryVariables
  >(EDIT_CROP_BUFFER_PERCENT_DATA, {
    fetchPolicy: 'network-only',
  });
  const [editCropBuffer, { loading: editCropLoading }] = useMutation<
    EditCropBufferPercentMutation,
    EditCropBufferPercentMutationVariables
  >(EDIT_CROP_BUFFER_PERCENT_MUTATION);
  const { timezone } = useUserContext();
  const { todayStart, sevenDaysFromTodayStart } = getTimeRanges(timezone);

  React.useEffect(() => {
    if (isOpen) {
      void fetchCrop({
        variables: { id: cropId },
        onCompleted: (cropData) => {
          const crop = cropData?.crop;
          if (crop != null) {
            setBufferPercent(crop.buffer_percentage.toString());
          }
        },
        onError: (errorIn) => {
          enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        },
      });
    }
  }, [isOpen, cropId, fetchCrop]);

  const disableFields = cropDataLoading || editCropLoading;

  const [bufferPercent, setBufferPercent] = React.useState('');
  const [bufferPercentValidation, setBufferPercentValidation] = React.useState('');
  const onChangeBufferPercent = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setBufferPercent(e.target.value);
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setBufferPercentValidation(msg),
      validateGTE: 0,
    });
  };
  const onBufferPercentBlur = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    validateNumInput({
      validateInt: true,
      event: e,
      setValidationMessage: (msg) => setBufferPercentValidation(msg),
      validateGTE: 0,
    });
  };

  const onSubmit = (_e?: React.MouseEvent | React.TouchEvent) => {
    void editCropBuffer({
      refetchQueries: [
        {
          query: DASHBOARD_QUERY,
          variables: {
            todayStart: todayStart.toString(),
            sevenDaysFromTodayStart: sevenDaysFromTodayStart.toString(),
          },
        },
      ],
      variables: {
        crop_id: getNumericID(cropId),
        buffer_percentage: bufferPercent === '' ? 0 : Number.parseInt(bufferPercent),
      },
      onCompleted: (dataIn) => {
        if (dataIn !== undefined && dataIn.editCropBufferPercent != null) {
          enqueueSnackbar('Updated crop safety margin', { variant: 'success' });
          onSuccess();
        } else {
          enqueueSnackbar('Error editing crop, no data returned', { variant: 'error', autoHideDuration: 10000 });
        }
        setBufferPercent('');
        setBufferPercentValidation('');
      },
      onError: (errorIn) => {
        enqueueSnackbar(errorIn.message, { variant: 'error', autoHideDuration: 10000 });
        clientLogger.error({ title: 'edit_crop_error', message: errorIn.message });
      },
    });
  };

  const hasInputValidationError: boolean = bufferPercentValidation !== '';
  const requiredFieldsFilled: boolean = bufferPercent !== '';
  const isSubmitDisabled: boolean = !requiredFieldsFilled || hasInputValidationError || disableFields;
  const onEnter = () => {
    if (!isSubmitDisabled) {
      onSubmit();
    }
  };
  const onCancelKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      onClose();
    }
  };
  const onSubmitKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (!isSubmitDisabled) {
        onSubmit();
      }
    }
  };

  const form = (
    <Box display="flex" flexDirection="column" sx={{ m: 1 }} gap={2}>
      <Box display="flex" flexDirection="row" flexWrap="nowrap" alignItems="center" gap={2}>
        <TextField
          aria-describedby="safety-margin"
          id="safety-margin"
          label="Safety Margin (%)"
          value={bufferPercent}
          onChange={onChangeBufferPercent}
          onBlur={onBufferPercentBlur}
          onEnter={onEnter}
          subtext={bufferPercentValidation}
          error={bufferPercentValidation !== ''}
          type="number"
          inputMode="numeric"
          numberMin={0}
          hasSubtext
          disabled={disableFields}
        />
        %
        <Help tooltip="Microgreen Manager will create this much extra for this crop for every tray needed to fulfill an order. So if safety margin is 30%, and an order will need 10.0 trays for this crop for the given inputs of that order, then Microgreen Manager will add an extra 3.0 trays ontop of that" />
      </Box>
      <Typography variant="body1">
        Note that editing a crop's safety margin will automatically update tray counts for planned plantings, but not
        for in progress or completed plantings.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        onClose();
      }}
      maxWidth="md"
    >
      <DialogTitle>
        <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">{formatCropDialogTitle('Edit Safety Margin', cropName)}</Typography>
          <ExitDialogButton dontShiftUp onCloseDialog={onClose} />
        </Box>
      </DialogTitle>
      <DialogContent sx={{ padding: 1 }}>
        {dataError ? <ErrorView error={dataError} /> : cropDataLoading || data?.crop == null ? <PageLoading /> : form}
      </DialogContent>
      <DialogActions disableSpacing sx={{ flexDirection: { md: 'row', xs: 'column' }, alignItems: 'center' }}>
        <Button
          sx={{ width: { xs: '100%', md: '50%' } }}
          variant="outlined"
          onClick={onClose}
          onKeyDown={onCancelKeyDown}
        >
          Cancel
        </Button>
        <Button
          sx={{ width: { xs: '100%', md: '50%' }, ml: { md: 1 }, mt: { xs: 1, md: 0 } }}
          variant="contained"
          onClick={onSubmit}
          type="submit"
          disabled={isSubmitDisabled}
          onKeyDown={onSubmitKeyDown}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
